import { useState, useEffect } from "react";
import axios from "axios";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MiniDrawer from "../../../components/MiniDrawer/MiniDrawer";
import { useNavigate } from "react-router-dom";
import ExcelExport from "../../../components/ExcelExport/ExcelExportAccounts";
import Wrapper from "../../../components/UI/Wrapper";

import SalesIcon from "../../../Icons/SalesAmount.png";
import RatioIcon from "../../../Icons/Ratio.png";

const FINANCIAL_YEARS = [ "2026 - 2027","2025 - 2026",  "2024 - 2025", "2023 - 2024", "2022 - 2023", "2021 - 2022"];

// Extract start year from "2024 - 2025" => "2024"
const extractYear = (fy) => fy.split(" - ")[0].trim();

const fmt = (val) => {
    const num = Number(val || 0);

    if (num === 0) return "₹ 0";

    return num < 0
        ? `- ₹ ${Math.abs(num).toLocaleString("en-IN")}`
        : `₹ ${num.toLocaleString("en-IN")}`;
};

const fmtSummary = (val) =>
    !val ? "₹ 0" : "₹ " + Number(val).toLocaleString("en-IN");








const gpRatio = (sales, cogs) => {
    if (!sales) return null;
    return Math.round(((sales - cogs) / sales) * 100);
};

const KPICard = ({ icon, label, value }) => (
    <div style={styles.kpiCard}>
        <div style={styles.kpiIcon}>{icon}</div>
        <div>
            <p style={styles.kpiLabel}>{label}</p>
            <p style={styles.kpiValue}>{value}</p>
        </div>
    </div>
);

const SVGReceipt = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EEEDFE" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 2h16v20l-2-2-2 2-2-2-2 2-2-2-2 2z" /><line x1="8" y1="9" x2="16" y2="9" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="12" y2="17" />
    </svg>
);
const SVGWarehouse = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EEEDFE" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9.5L12 4l9 5.5V20H3z" /><rect x="9" y="14" width="6" height="6" /><line x1="9" y1="14" x2="9" y2="20" />
    </svg>
);
const SVGCoin = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EEEDFE" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" /><path d="M12 7v10M9.5 9.5c0-1.4 1.1-2.5 2.5-2.5s2.5 1.1 2.5 2.5-2.5 2-2.5 2 2.5.6 2.5 2.5S13.4 17 12 17s-2.5-1.1-2.5-2.5" />
    </svg>
);
const SVGPercent = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EEEDFE" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="8" cy="8" r="2" /><circle cx="16" cy="16" r="2" /><line x1="5" y1="19" x2="19" y2="5" />
    </svg>
);
const SVGDownload = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
);
const SVGHome = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9.5L12 4l9 5.5V20H3z" /><rect x="9" y="14" width="6" height="6" />
    </svg>
);
const SVGChevron = () => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
    </svg>
);

export default function ProfitLossReport() {
    const baseAPI = process.env.REACT_APP_API_URL;
    const storedToken = JSON.parse(localStorage.getItem("token"));

    const [fy, setFy] = useState(FINANCIAL_YEARS[0]);
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const headers = {
        Authorization: `Bearer ${storedToken}`,
        "Content-Type": "application/json",
    };

    const fetchReport = async (selectedFy) => {
        setLoading(true);
        setError(null);

        try {
            const financialYear = extractYear(selectedFy);

            const [summaryRes, detailRes] = await Promise.all([
                axios.get(
                    `${baseAPI}userget/GetProfitLossReportSummary?FinancialYear=${financialYear}`,
                    { headers }
                ),

                axios.get(
                    `${baseAPI}userget/GetProfitLossReportDetail?FinancialYear=${financialYear}`,
                    { headers }
                ),
            ]);

            // Summary API
            setSummary(summaryRes?.data?.data?.[0] || null);

            // Detail API
            setRows(detailRes?.data?.data || []);
        } catch (err) {
            console.error("GetProfitLossReport error:", err);
            setError("Failed to load report. Please try again.");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchReport(fy);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fy]);

    // ── Normalise row keys regardless of API casing ────────────────────
    const normalise = (r, index) => {
        return {
            id: index + 1,
            serviceType: r.ShipmentType || "",
            sales: Number(r.Sales || 0),
            grossSales: Number(r.GrossSales || 0),
            cogs: Number(r.CostOfGoodSold || 0),
            gp: Number(r.GrossProfitYearly || 0),
            operationCost: Number(r.OperationCostYearly || 0),
            netProfit: Number(r.NetProfitYearly || 0),
            ratio: Number(r.GrossProfitRatio || 0),
        };
    };

    const normRows = rows.map((r, i) => normalise(r, i));
    const totals = normRows.reduce(
        (acc, r) => ({
            sales: acc.sales + r.sales,
            grossSales: acc.grossSales + r.grossSales,
            cogs: acc.cogs + r.cogs,
            gp: acc.gp + r.gp,
            operationCost: acc.operationCost + r.operationCost,
            netProfit: acc.netProfit + r.netProfit,
        }),
        {
            sales: 0,
            grossSales: 0,
            cogs: 0,
            gp: 0,
            operationCost: 0,
            netProfit: 0,
        }
    );
    const totalRatio = totals.sales ? Math.round((totals.gp / totals.sales) * 100) : 0;

    const kpiSales = summary?.TotalSales ?? totals.sales;
    const kpiCOGS = summary?.TotalCOGS ?? totals.cogs;
    const kpiGP = summary?.TotalGrossProfit ?? totals.gp;
    const kpiRatio = summary?.TotalGPRatio ?? 0;


    return (
        <div style={styles.root}>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } } .pl-spin { animation: spin 0.8s linear infinite; transform-origin: center; }`}</style>

            {/* Breadcrumb */}
            <div style={styles.breadcrumb}>
                <SVGHome /><SVGChevron />
                <span>Accounts &amp; Finance</span><SVGChevron />
                <span>Reports</span><SVGChevron />
                <span style={styles.breadcrumbActive}>Profit &amp; Loss</span>
            </div>

            {/* Top bar */}
            <div style={styles.topbar}>
                <h1 style={styles.pageTitle}>Profit &amp; Loss</h1>
                <div style={styles.topbarRight}>
                    <select value={fy} onChange={(e) => setFy(e.target.value)} style={styles.fySelect} disabled={loading}>
                        {FINANCIAL_YEARS.map((y) => <option key={y}>{y}</option>)}
                    </select>
                    <button style={styles.dlBtn} disabled={loading}>
                        <SVGDownload /> Download Excel
                    </button>
                </div>
            </div>

            {/* Error banner */}
            {error && (
                <div style={styles.errorBox}>
                    ⚠️ {error}
                    <button style={styles.retryBtn} onClick={() => fetchReport(fy)}>Retry</button>
                </div>
            )}

            {/* KPI Cards */}
            <div style={styles.kpiGrid}>
                <KPICard icon={<SVGReceipt />} label="Total Sales Amount" value={loading ? "—" : fmtSummary(kpiSales)} />
                <KPICard icon={<SVGWarehouse />} label="Total C.O.G.S Amount" value={loading ? "—" : fmtSummary(kpiCOGS)} />
                <KPICard icon={<SVGCoin />} label="Total G.P. Amount" value={loading ? "—" : fmtSummary(kpiGP)} />
                <KPICard icon={<SVGPercent />} label="Total G.P. Ratio (%)" value={loading ? "—" : `${kpiRatio}%`} />
            </div>

            {/* Table */}
            <div style={styles.tableCard}>
                <div style={styles.tableHeader}>
                    <span style={styles.tableTitle}>Profit &amp; Loss List</span>
                </div>

                {loading ? (
                    <div style={styles.loadingBox}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#534AB7" strokeWidth="2.5" strokeLinecap="round">
                            <path className="pl-spin" d="M12 2a10 10 0 0 1 10 10" />
                        </svg>
                        <span style={styles.loadingText}>Loading report…</span>
                    </div>
                ) : (
                    <div style={styles.tableWrapper}>
                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.thead}>
                                    {[
                                        { label: "#", align: "left" },
                                        { label: "Service Type", align: "left" },
                                        { label: "Sales", align: "right" },
                                        { label: "Gross Sales", align: "right" },
                                        { label: "Cost of Goods Sold", align: "right" },
                                        { label: "Gross Profit Yearly", align: "right" },
                                        { label: "Op. Cost Yearly", align: "right" },
                                        { label: "Net Profit Yearly", align: "right" },
                                        { label: "GP Ratio", align: "right" },
                                    ].map((h, i) => (
                                        <th key={i} style={{ ...styles.th, textAlign: h.align }}>{h.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {normRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} style={styles.emptyCell}>No data found for FY {fy}.</td>
                                    </tr>
                                ) : (
                                    normRows.map((row, idx) => {
                                        const hasData = row.sales > 0;
                                        return (
                                            <tr key={idx} style={styles.tr}>
                                                <td style={{ ...styles.td, color: hasData ? "#534AB7" : "#b0aac8", fontWeight: hasData ? 500 : 400 }}>{row.id}</td>
                                                <td style={{ ...styles.td, color: hasData ? "#1a1a2e" : "#888" }}>{row.serviceType}</td>
                                                <td style={{ ...styles.tdNum, color: hasData ? "#1a1a2e" : "#ccc" }}>{fmt(row.sales) ?? "-"}</td>
                                                <td style={{ ...styles.tdNum, color: hasData ? "#1a1a2e" : "#ccc" }}>{fmt(row.grossSales) ?? "-"}</td>
                                                <td style={{ ...styles.tdNum, color: hasData ? "#1a1a2e" : "#ccc" }}>{fmt(row.cogs) ?? "-"}</td>
                                                <td style={{ ...styles.tdNum, color: !hasData ? "#ccc" : row.gp < 0 ? "#A32D2D" : "#1a1a2e" }}>
                                                    {fmt(row.gp) ?? "₹ 0"}
                                                </td>
                                                <td style={{ ...styles.tdNum, color: "#aaa" }}>{fmt(row.operationCost) ?? "₹ 0"}</td>
                                                <td style={{ ...styles.tdNum, color: !hasData ? "#ccc" : row.gp < 0 ? "#A32D2D" : "#1a1a2e" }}>
                                                    {fmt(row.netProfit) ?? "₹ 0"}
                                                </td>
                                                <td style={styles.tdNum}>
                                                    {row.ratio !== null ? (
                                                        <span style={{ ...styles.ratioBadge, ...(row.ratio < 0 ? styles.ratioBadgeNeg : styles.ratioBadgePos) }}>
                                                            {row.ratio}%
                                                        </span>
                                                    ) : "-"}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                            {normRows.length > 0 && (
                                <tfoot>
                                    <tr style={styles.tfootRow}>
                                        <td style={styles.tfootTd} colSpan={2}>
                                            Total
                                        </td>

                                        <td style={styles.tfootTdNum}>
                                            {fmt(totals.sales)}
                                        </td>

                                        <td style={styles.tfootTdNum}>
                                            {fmt(totals.grossSales)}
                                        </td>

                                        <td style={styles.tfootTdNum}>
                                            {fmt(totals.cogs)}
                                        </td>

                                        <td style={styles.tfootTdNum}>
                                            {fmt(totals.gp)}
                                        </td>

                                        <td style={styles.tfootTdNum}>
                                            {fmt(totals.operationCost)}
                                        </td>

                                        <td style={styles.tfootTdNum}>
                                            {fmt(totals.netProfit)}
                                        </td>

                                        <td style={styles.tfootTdNum}>
                                            <span
                                                style={{
                                                    ...styles.ratioBadge,
                                                    ...(totalRatio < 0
                                                        ? styles.ratioBadgeNeg
                                                        : styles.ratioBadgePos),
                                                }}
                                            >
                                                {totalRatio}%
                                            </span>
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = {
    root: { fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: "#f5f4fb", minHeight: "100vh", padding: "24px 32px", boxSizing: "border-box" },
    breadcrumb: { display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#888", marginBottom: "16px" },
    breadcrumbActive: { color: "#534AB7", fontWeight: 500 },
    topbar: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" },
    pageTitle: { fontSize: "22px", fontWeight: 600, color: "#1a1a2e", margin: 0 },
    topbarRight: { display: "flex", alignItems: "center", gap: "10px" },
    fySelect: { fontSize: "13px", border: "1px solid #d0cce8", borderRadius: "8px", padding: "7px 14px", background: "#fff", color: "#444", outline: "none", cursor: "pointer" },
    dlBtn: { display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", padding: "7px 16px", border: "1px solid #d0cce8", borderRadius: "8px", background: "#fff", color: "#444", cursor: "pointer", fontFamily: "inherit" },
    errorBox: { background: "#FCEBEB", border: "1px solid #f5c6c6", borderRadius: "8px", padding: "10px 16px", color: "#A32D2D", fontSize: "13px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "12px" },
    retryBtn: { marginLeft: "auto", padding: "4px 12px", fontSize: "12px", border: "1px solid #A32D2D", borderRadius: "6px", background: "transparent", color: "#A32D2D", cursor: "pointer", fontFamily: "inherit" },
    kpiGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "24px" },
    kpiCard: { background: "#EEEDFE", borderRadius: "12px", padding: "16px 18px", display: "flex", alignItems: "center", gap: "14px" },
    kpiIcon: { width: "42px", height: "42px", borderRadius: "50%", background: "#534AB7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    kpiLabel: { fontSize: "11px", color: "#3C3489", margin: "0 0 4px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" },
    kpiValue: { fontSize: "16px", fontWeight: 700, color: "#26215C", margin: 0, whiteSpace: "nowrap" },
    tableCard: { background: "#fff", borderRadius: "12px", border: "1px solid #e8e4f8", overflow: "hidden" },
    tableHeader: { background: "#534AB7", padding: "13px 18px" },
    tableTitle: { color: "#EEEDFE", fontSize: "14px", fontWeight: 600, letterSpacing: "0.2px" },
    loadingBox: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 0", gap: "12px" },
    loadingText: { fontSize: "13px", color: "#534AB7" },
    tableWrapper: { overflowX: "auto" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "13px", minWidth: "900px" },
    thead: { background: "#f8f7fd" },
    th: { padding: "11px 14px", fontSize: "11.5px", fontWeight: 600, color: "#6b63a8", borderBottom: "1px solid #ece9f8", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.3px" },
    tr: { borderBottom: "1px solid #f2f0fb" },
    td: { padding: "11px 14px", fontSize: "13px", color: "#333" },
    tdNum: { padding: "11px 14px", fontSize: "13px", textAlign: "right", color: "#333" },
    emptyCell: { padding: "40px", textAlign: "center", color: "#aaa", fontSize: "13px" },
    ratioBadge: { display: "inline-block", borderRadius: "20px", padding: "3px 10px", fontSize: "12px", fontWeight: 600 },
    ratioBadgePos: { background: "#EAF3DE", color: "#3B6D11" },
    ratioBadgeNeg: { background: "#FCEBEB", color: "#A32D2D" },
    tfootRow: { background: "#f3f1fc", borderTop: "2px solid #d8d2f0" },
    tfootTd: { padding: "12px 14px", fontWeight: 700, fontSize: "13px", color: "#26215C" },
    tfootTdNum: { padding: "12px 14px", fontWeight: 700, fontSize: "13px", textAlign: "right", color: "#26215C" },
};
